import { VuexModule, Module, Action, Mutation } from 'vuex-module-decorators'
import InventoryApi from '@/api/inventory-api'
import { TimeUtil } from '@/helpers/TimeUtil'
import { commonModule, companyModule } from '@/store/modules/store-accessor'
import i18n from '@/i18n';
import { deepTrim, itemUnitAbbreviation, warrantyPayloadOption, warrantyValueToMonths } from '@/helpers/data';

@Module({ namespaced: true, name: "items" })
export default class ItemsModule extends VuexModule {
  public items: ItemDto[] = [];
  public installationItems: InstallationItemDto[] = [];
  public isLoadingTable = true;
  public filters = "";
  public installationPrimaryItems: ItemDto[] = [];
  public allCompanyPrimaryItems: ItemDto[] = [];
  public search = "";
  public queryObject: any = {};
  public itemsUnitsCombo = [];
  public loadingItemsCombo = true;

  @Action({ commit: "setItems" })
  async getAllInventoryItems(query: string): Promise<ItemDto[]> {
    this.setIsLoadingTable(true);
    const itemsApi = new InventoryApi();
    const items = await itemsApi.getAllInventoryItems(query);
    this.setIsLoadingTable(false);
    return items;
  }

  @Mutation
  public setItems(items: ItemDto[]): void {
    try {
      this.items = items;
      this.installationItems = items.map((ii) => ({
        id: ii.id,
        make: ii.make,
        model: ii.model,
        name: `${ii.type_name} | ${ii.make} | ${ii.model ?? "-"}`,
        item_type_id: ii.item_type_id,
        warranty: ii.warranty,
        warranty_option: ii.warranty_option,
      }));
      this.isLoadingTable = false;
    } catch (error) {
      this.isLoadingTable = false;
    }
  }

  @Action({ commit: "setInstallationPrimaryItems" })
  async getInstallationPrimaryInventoryItems(
    installationId: string
  ): Promise<ItemDto[]> {
    this.setIsLoadingTable(true);
    const itemsApi = new InventoryApi();
    const items = await itemsApi.getInstallationPrimaryInventoryItems(
      installationId
    );
    this.setIsLoadingTable(false);
    return items.map((x: any) => {
      x.hidden_id = (Math.random() + 1).toString(36).substring(7);
      return x;
    });
  }

  @Mutation
  public setInstallationPrimaryItems(installationPrimaryItems: ItemDto[]): void {
    const installationItemDescriptionIsEnabled = (companyModule.company?.preferences as any)?.installation_item_description;

    if (installationItemDescriptionIsEnabled) {
      this.installationPrimaryItems = installationPrimaryItems.map(item => ({
        ...item,
        primary_items: [`${item.item_description ? item.item_description + ' | ' : ''}${item.primary_items}`]
      }));
    } else {
      this.installationPrimaryItems = installationPrimaryItems;
    }
  }

  @Action({ commit: "setAllCompanyPrimaryItems" })
  async getAllCompanyPrimaryInventoryItems(): Promise<ItemDto[]> {
    this.setIsLoadingTable(true);
    const itemsApi = new InventoryApi();
    const items = await itemsApi.getAllCompanyPrimaryInventoryItems();
    this.setIsLoadingTable(false);
    return items;
  }

  @Mutation
  public setAllCompanyPrimaryItems(allCompanyPrimaryItems: ItemDto[]): void {
    this.allCompanyPrimaryItems = allCompanyPrimaryItems;
  }

  @Mutation
  public clearItems(): void {
    this.items = [];
  }

  @Action
  async createItem(data: Record<string, any>) {
    data.item_type_id = data.item_type_id.id;

    if (data.model == "") {
      delete data.model;
    }
    if (data.model_year == "") {
      delete data.model_year;
    }
    if (data.notes == "") {
      delete data.notes;
    }
    if (data.warranty == "" || data.warranty == null) {
      delete data.warranty;
      delete data.warranty_option;
    } else {
      data.warranty_option = warrantyPayloadOption(data.warranty, data.warranty_option);
      data.warranty = warrantyValueToMonths(data.warranty, data.warranty_option);
    }

    if(data.is_unit_based) {
      data.unit = data.unit.enum_text;
    } else {
      delete data.unit;
    }

    const itemsApi = new InventoryApi();

    data = deepTrim(data);
    await itemsApi.createInventoryItem(data);
    await this.getAllInventoryItems(this.filters);
  }

  @Action
  async deleteItem(item: Record<string, any>) {
    const itemsApi = new InventoryApi();
    await itemsApi.deleteInventoryItem(item.id);
    await this.getAllInventoryItems(this.filters);
  }

  @Action
  async editItem(item: Record<string, any>) {
    item.item_type_id = item.item_type_id.id;

    if (item.model == "") {
      delete item.model;
    }
    if (item.model_year == "") {
      delete item.model_year;
    }
    if (item.warranty == "" || item.warranty == null) {
      delete item.warranty;
      delete item.warranty_option;
    } else {
      item.warranty_option = warrantyPayloadOption(item.warranty, item.warranty_option);
      item.warranty = warrantyValueToMonths(item.warranty, item.warranty_option);
    }

    if(item.is_unit_based) {
      item.unit = item.unit.enum_text;
    } else {
      delete item.unit;
    }

    const itemsApi = new InventoryApi();

    item = deepTrim(item);
    await itemsApi.updateInventoryItem(item.id, item);
    await this.getAllInventoryItems(this.filters);
  }

  @Mutation
  public setIsLoadingTable(isLoadingTable: boolean): void {
    this.isLoadingTable = isLoadingTable;
  }

  @Mutation
  public setFilters(filters: string): void {
    this.filters = filters;
  }

  @Mutation
  public setQueryObject(queryObject: any): void {
    this.queryObject = queryObject;
  }

  @Mutation
  public setSearch(search: string): void {
    this.search = search;
  }

  @Action({ commit: 'setItemsUnitsCombo'})
  async getItemsUnitsCombo (): Promise<any> {
    const itemsApi = new InventoryApi()
    this.setItemsComboLoader(true);
    const itemsUnitsCombo = await itemsApi.getInventoryItemsUnitsCombo();
    this.setItemsComboLoader(false);

    return itemsUnitsCombo;
  }

  @Mutation
  public setItemsUnitsCombo (itemsUnitsCombo: any): void {
    const acceptDecimals = [
      'METERS', 'CENTIMETERS', 'SQUAREMETERS', 'MILLIMETERS',
      'SQUARECENTIMETERS', 'SQUAREMILLIMETERS', 'CUBICMETERS',
      'LITRES', 'MILLILITER', 'TONNE', 'KILOGRAM', 'GRAM',
      'MILLIGRAM'
    ]; // PIECES is not here

    this.itemsUnitsCombo = itemsUnitsCombo.map((unit: any) => (
      { text: `${i18n.t(unit.item_units.toLowerCase())} (${itemUnitAbbreviation(unit.item_units)})`,
        enum_text: unit.item_units,
        decimals: acceptDecimals.includes(unit.item_units)
      }));
  }

  @Mutation
  public setItemsComboLoader (loadingItemsCombo: boolean): void {
    this.loadingItemsCombo = loadingItemsCombo;
  }
}
